import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Session, User, SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from './supabase';
import { captureException, identifyUser, logoutUser } from './telemetry/posthog/frontend';

interface SupabaseContextType {
  supabase: SupabaseClient;
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** Whether the latest session lookup failed before authentication could be verified. */
  sessionError: boolean;
  /** Re-run the authoritative session lookup after a recoverable failure. */
  retrySession: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export const SupabaseProvider: React.FC<SupabaseProviderProps> = ({ children }) => {
  const [supabase] = useState(() => getSupabase());
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const identifiedUserIdRef = useRef<string | null>(null);
  const sessionRequestGenerationRef = useRef(0);

  const syncPostHogIdentity = useCallback((nextSession: Session | null) => {
    const nextUserId = nextSession?.user?.id ?? null;

    if (identifiedUserIdRef.current === nextUserId) {
      return;
    }

    if (!nextUserId) {
      if (identifiedUserIdRef.current) {
        logoutUser();
        identifiedUserIdRef.current = null;
      }
      return;
    }

    // An explicit logout before re-identifying is required for posthog
    // otherwise, the 2 identities will be merged to a single one.
    logoutUser();
    identifyUser(nextUserId, {
      email: nextSession?.user?.email,
    });
    identifiedUserIdRef.current = nextUserId;
  }, []);

  /** Resolve the current session without exposing provider details to consumers. */
  const retrySession = useCallback(async (): Promise<void> => {
    const requestGeneration = ++sessionRequestGenerationRef.current;
    setLoading(true);
    setSessionError(false);
    try {
      const { data: { session: nextSession }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (requestGeneration !== sessionRequestGenerationRef.current) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      syncPostHogIdentity(nextSession);
    } catch {
      if (requestGeneration !== sessionRequestGenerationRef.current) return;
      captureException(
        new Error('Supabase session lookup failed'),
        'SupabaseProvider.getSession',
        {},
      );
      setSession(null);
      setUser(null);
      setSessionError(true);
    } finally {
      if (requestGeneration === sessionRequestGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [supabase, syncPostHogIdentity]);

  useEffect(() => {
    // Get initial session
    void retrySession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // getSession is the authority for a null initialization. Supabase also
        // emits INITIAL_SESSION with null when that internal read throws, so
        // treating it as sign-out would erase the recoverable lookup error. A
        // non-null initial session is still valid authenticated evidence.
        if (event === 'INITIAL_SESSION' && !session) return;

        // An auth event is newer authoritative state than any pending lookup.
        sessionRequestGenerationRef.current += 1;
        setSession(session);
        setUser(session?.user ?? null);
        setSessionError(false);
        syncPostHogIdentity(session);
        setLoading(false);
      }
    );

    return () => {
      sessionRequestGenerationRef.current += 1;
      subscription.unsubscribe();
    };
  }, [retrySession, supabase, syncPostHogIdentity]);

  const signOut = async () => {
    logoutUser();
    identifiedUserIdRef.current = null;
    await supabase.auth.signOut();
  };

  const value: SupabaseContextType = {
    supabase,
    session,
    user,
    loading,
    sessionError,
    retrySession,
    signOut,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

export const useSupabase = (): SupabaseContextType => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

export default SupabaseProvider;
